import { useEffect, useState } from 'react';
import groupBy from 'lodash/groupBy';

import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import { Action } from 'redux';
import { Tabs } from 'antd';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { injectIntl, WrappedComponentProps, FormattedMessage } from 'react-intl';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';

import { MOBILE_MEDIA_POINT, Routes } from '@share/constants';
import { IQuoteState, getQuotes } from '@share/store/slices';
import { RootState, getKeyQuotesFromPath, LoginType } from '@share/utils';
import { ILoginState } from '@share/store/slices';
import { QuoteModalCompare, SearchTable } from '@components';
import { NULL_VALUE } from '@constants';

import { QuoteCondosModalCompare } from '../quote-condos-modal-compare';
import { GetMyQuotesTabs, GetQuoteDatasourceData, HISTORICAL_KEY, HOTELS_KEY } from './utils';

import './style.scss';
import { isEmpty } from 'lodash';

interface IMapStateToProps {
  loginStore: ILoginState;
  quoteStore: IQuoteState;
}

interface IMapDispatchToProps {
  get: (id: any) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, RouteComponentProps, WrappedComponentProps { }

function MyQuotesComponent(props: IProps) {
  const { loginStore, quoteStore } = props;
  const { account } = loginStore;
  const { loading, result } = quoteStore;
  const { quotesHotels, quotesCondos, quotesHistorical } = result;
  const [visibleHotels, setVisibleHotels] = useState(false);
  const [visibleCondos, setVisibleCondos] = useState(false);
  const [isHistorical, setIsHistorical] = useState(false);
  const [quoteBase, setQuoteBase] = useState<string | null>(null);
  
  const isMobile = document.body.offsetWidth <= MOBILE_MEDIA_POINT;

  if (!account || account.type === LoginType.Public) {
    props.history.push(Routes.NotAuthorized);
  }

  useEffect(() => {
    if (!loginStore.loading) {
      const param = getKeyQuotesFromPath();
      props.get(getKeyQuotesFromPath());
      setVisibleHotels(!!param);
    }
  }, [loginStore.loading])

  const handleOnRefresh = () => {
    const { loginStore } = props;
    const { user } = loginStore;
    if (user) {
      props.get(getKeyQuotesFromPath());
    }
  };

  let dataSourceHotels = [];
  let dataSourceCondos = [];
  let dataSourceHistorical = [];

  if (quotesHotels.length) {
    const grouped = groupBy(quotesHotels, quote => quote.quoteBaseId);
    dataSourceHotels = Object.entries(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0]));
  }

  if (quotesCondos.length) {
    const grouped = groupBy(quotesCondos, quote => quote.quoteBaseId);
    dataSourceCondos = Object.entries(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quotesCondos));
  }

  if (quotesHistorical.length) {
    const grouped = groupBy(quotesHistorical, quote => quote.quoteBaseId);
    dataSourceHistorical = Object.entries(grouped).map(([_key, quote]) => GetQuoteDatasourceData(quote[0], quotesHistorical));
  }

  const tabs = GetMyQuotesTabs(isMobile, dataSourceHotels, dataSourceCondos, dataSourceHistorical, setVisibleHotels, setVisibleCondos, setQuoteBase);
  
  const buttonTextColor = account?.buttonTextColor;
  const buttonBorder = account?.buttonBorder;
  const buttonBorderRadius = account?.buttonBorderRadius;
  const buttonColor = account?.buttonColor;

  const styleButtonBorderRadius = !isEmpty(buttonBorderRadius) ? { borderRadius: `${buttonBorderRadius}px` } : {};  
  const styleButtonBorder = !isEmpty(buttonBorder)? { ...styleButtonBorderRadius, border: buttonBorder } : { ...styleButtonBorderRadius };  
  const styleButtonColor = !isEmpty(buttonTextColor)? { ...styleButtonBorder, color: buttonTextColor } : { ...styleButtonBorder };  
  const styleButton = !isEmpty(buttonColor)? { ...styleButtonColor, background: buttonColor } : { ...styleButtonColor };  

  return (
    <div className={`quotes-wrapper ${isMobile ? 'mobile' : ''}`}>
      <div className="quotes-wrapper__refresh" onClick={handleOnRefresh}>
        <div className="action-button" style={styleButton}>
          <FontAwesomeIcon icon={faSyncAlt} size="1x"/>
          <span>Refresh</span>
        </div>
      </div>

      <Tabs defaultActiveKey={HOTELS_KEY} onChange={(key: string) => setIsHistorical(key === HISTORICAL_KEY)}>
        {tabs.map(({ title, columns, datasource, key }) => (
          <Tabs.TabPane key={key} tab={<FormattedMessage id={title} />}>
            <SearchTable
              title={NULL_VALUE}
              loading={loading}
              actions={NULL_VALUE}
              allColumns={columns as any}
              dataSource={datasource}
              pagination={{
                showSizeChanger: false,
                showQuickJumper: false
              }}
              filters={NULL_VALUE}
              totalOrderCount={datasource.length}
              rowKey={(row: any) => row.key ? `${row.key}` : `${row.quoteBase}`}
              hideRefresh={true}
              isFilterApplied={false}
              onFiltersChange={NULL_VALUE}
              onTableChange={NULL_VALUE}
              onResetFilters={NULL_VALUE}
              onApplyFilters={NULL_VALUE}
              onRefresh={handleOnRefresh}
            ></SearchTable>
          </Tabs.TabPane>
        ))}
      </Tabs>

      {(quotesHotels.length || quotesHistorical.length) ? <QuoteModalCompare isHistorical={isHistorical} visible={visibleHotels} quoteBase={quoteBase as string} onCancel={() => { setVisibleHotels(false); setQuoteBase(null) }} /> : null}
      {(quotesCondos.length || quotesHistorical.length) ? <QuoteCondosModalCompare isHistorical={isHistorical} visible={visibleCondos} quoteBase={quoteBase as string} onCancel={() => { setVisibleCondos(false); setQuoteBase(null) }} /> : null}
    </div>

  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore,
    quoteStore: state.quoteStore,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, undefined, Action>): IMapDispatchToProps => ({
  get: (id: any) => {
    dispatch(getQuotes(id));
  },
});

const ComponentIntl = injectIntl(MyQuotesComponent);

export const MyQuotes = connect(mapStateToProps, mapDispatchToProps)(withRouter(ComponentIntl));
